import React, { useState } from 'react';
import { Calendar, Clock, Pencil, Terminal, Trash2 } from 'lucide-react';
import DeleteJobModal from './DeleteJobModal';
import { CardMenu } from '../ui';
import { cn } from '../../lib/utils';

function getScheduleLabel(job) {
  const schedule = job?.schedule;
  if (!schedule) return 'manual';

  if (schedule.kind === 'every' && schedule.everyMs) {
    const minutes = Math.floor(schedule.everyMs / 60000);

    if (minutes >= 1440 && minutes % 1440 === 0) {
      const days = minutes / 1440;
      return `Every ${days} day${days > 1 ? 's' : ''}`;
    }

    if (minutes >= 60 && minutes % 60 === 0) {
      const hours = minutes / 60;
      return `Every ${hours} hour${hours > 1 ? 's' : ''}`;
    }

    return `Every ${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }

  if (schedule.kind === 'at' && schedule.atMs) {
    const formattedDate = new Date(schedule.atMs).toLocaleString('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    return `At ${formattedDate}`;
  }

  if (schedule.kind === 'cron') {
    return schedule.expr ?? '';
  }

  return 'manual';
}

const scheduleIcons = { every: Clock, at: Calendar, cron: Terminal };

export function CronJobList({ jobs, onToggle, onEdit, loading }) {
  const [deleteJob, setDeleteJob] = useState(null);
  const responseJobs = jobs?.cronjobs || jobs?.data || jobs;
  const cronjobs = Array.isArray(responseJobs) ? responseJobs : responseJobs?.cronjobs || [];

  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-20 rounded-lg bg-shell-card border border-shell-card-border animate-pulse" />
        ))}
      </div>
    );
  }

  if (!cronjobs.length) {
    return <div className="py-20 text-center text-muted text-sm">No scheduled tasks yet.</div>;
  }

  return (
    <div className="space-y-3">
      {cronjobs.map(job => {
        const ScheduleIcon = scheduleIcons[job?.schedule?.kind] || Clock;
        const isActive = !!(job?.enabled ?? job?.enable);

        return (
          <div
            key={job?.id || job?.job_id}
            className="flex items-center gap-4 p-5 rounded-lg bg-shell-card border border-shell-card-border hover:border-[#d4d4d4] hover:bg-[#f7f6f2] transition-colors group">
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: '#F59E0B1A' }}>
              <Clock className="w-5 h-5 text-[#F59E0B]" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-semibold text-ink truncate">{job.name}</h3>
                <span
                  className={cn(
                    'text-[10px] font-medium px-2 py-0.5 rounded',
                    isActive ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-black/5 text-muted',
                  )}>
                  {isActive ? 'ACTIVE' : 'DISABLED'}
                </span>
              </div>
              <p className="text-xs text-muted truncate">{job?.payload?.message || 'No description'}</p>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-muted">
                <span className="flex items-center gap-1">
                  <ScheduleIcon className="w-3 h-3" />
                  {getScheduleLabel(job)}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onToggle?.(job)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                isActive
                  ? 'bg-black/5 text-ink hover:bg-black/10'
                  : 'bg-primary/15 text-ink hover:bg-primary/25',
              )}>
              {isActive ? 'Pause' : 'Resume'}
            </button>

            <CardMenu
              items={[
                { label: 'Edit Task', icon: Pencil, onClick: () => onEdit?.(job) },
                { label: 'Delete Task', icon: Trash2, onClick: () => setDeleteJob(job), danger: true },
              ]}
            />
          </div>
        );
      })}

      {deleteJob && <DeleteJobModal onClose={() => setDeleteJob(null)} data={deleteJob} />}
    </div>
  );
}
